/**
 * Runtime configuration for the customer portal.
 *
 * The API base URL is fetched from `/config.json` at boot instead of being
 * baked in at build time. One image therefore runs against any API instance —
 * changing the API hostname is a container restart, not a rebuild.
 *
 * Who writes config.json:
 *   * Production — the container entrypoint, from the `API_BASE_URL` env var.
 *     The image ships WITHOUT a config.json (the Dockerfile deletes the dev
 *     one after the build), so a broken entrypoint yields a loud boot error
 *     rather than a silently wrong endpoint.
 *   * Dev (`npm run dev`) — `public/config.json`, served by vite as-is.
 */

export interface RuntimeConfig {
  /** Absolute origin of the API, no trailing slash. E.g. `https://api.example.com`. */
  readonly apiBaseUrl: string;
}

let cached: RuntimeConfig | null = null;

/**
 * Thrown when the portal cannot determine a usable API endpoint. Deliberately
 * fatal: there is no safe default. Guessing an endpoint is what shipped a
 * bundle that probed every visitor's own machine (see assertSafeApiBaseUrl).
 */
export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigError";
  }
}

/**
 * Rejects an API base URL that is structurally impossible for the origin the
 * portal is being served from.
 *
 * Rationale: a loopback endpoint resolves to the *visitor's* machine, not
 * ours. Chrome surfaces that as a Local Network Access permission prompt —
 * a public page asking to reach devices on the user's LAN. That must never
 * leave the developer's laptop, so we fail the boot instead.
 *
 * @param apiBaseUrl  the candidate endpoint from config.json
 * @param pageOrigin  where the portal itself is served from (window.location.origin)
 * @throws ConfigError when the combination cannot be legitimate
 */
export function assertSafeApiBaseUrl(apiBaseUrl: string, pageOrigin: string): void {
  let api: URL;
  try {
    api = new URL(apiBaseUrl);
  } catch {
    throw new ConfigError(
      `apiBaseUrl must be an absolute URL, got "${apiBaseUrl}". ` +
        `Expected something like https://api.example.com — set API_BASE_URL accordingly.`,
    );
  }

  if (api.protocol !== "http:" && api.protocol !== "https:") {
    throw new ConfigError(
      `apiBaseUrl must use http or https, got "${api.protocol}" in "${apiBaseUrl}".`,
    );
  }

  const page = new URL(pageOrigin);

  // The load-bearing rule. A local API address resolves on the *visitor's*
  // machine, so it can only be legitimate when the portal itself is also
  // being served locally: dev (localhost -> localhost) and intranet
  // deployments (10.x -> 10.x) are fine; a public page reaching for a local
  // address never is. That combination is what shipped `https://api.localhost`
  // to production and made every visitor's browser prompt for LAN access.
  if (isLocalHostname(api.hostname) && !isLocalHostname(page.hostname)) {
    throw new ConfigError(
      `apiBaseUrl "${apiBaseUrl}" points at a local address, but this portal is served ` +
        `from the public origin "${pageOrigin}". A local address resolves to the visitor's ` +
        `own machine, not to the API — the browser will prompt them for local network ` +
        `access and every request will fail. Set API_BASE_URL to the public API origin.`,
    );
  }

  // The browser blocks this as mixed content regardless. Naming it here turns
  // an opaque console error plus a generic "not reachable" screen into a
  // stated cause on the boot-failure page.
  if (page.protocol === "https:" && api.protocol === "http:") {
    throw new ConfigError(
      `apiBaseUrl "${apiBaseUrl}" is http:// but this portal is served over https:// ` +
        `("${pageOrigin}"). The browser blocks that as mixed content. Use https:// for API_BASE_URL.`,
    );
  }
}

/**
 * True for hostnames that resolve inside the client's own machine or LAN:
 * loopback (RFC 6761 `localhost` and `*.localhost`, 127/8, ::1), the
 * unspecified address, link-local, and the RFC 1918 / ULA private ranges.
 *
 * These are exactly the targets browsers gate behind a Local Network Access
 * prompt — treating them as one category keeps the rule above honest for
 * intranet deployments instead of hard-coding a "localhost" special case.
 */
function isLocalHostname(hostname: string): boolean {
  // URL.hostname keeps IPv6 literals bracketed; strip for comparison.
  const host = hostname.toLowerCase().replace(/^\[/, "").replace(/\]$/, "");

  if (host === "localhost" || host.endsWith(".localhost")) return true;
  if (host === "::1" || host === "0.0.0.0" || host === "::") return true;
  if (host.startsWith("fe80:") || host.startsWith("fc") || host.startsWith("fd")) return true;

  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!v4) return false;
  const [a, b] = [Number(v4[1]), Number(v4[2])];
  if (a === 127 || a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

/**
 * Fetches and validates `/config.json`. Call once, before the first render.
 * Cached — later calls return the same object.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;

  let response: Response;
  try {
    response = await fetch("/config.json", { cache: "no-store" });
  } catch (cause) {
    throw new ConfigError(
      `Could not fetch /config.json: ${(cause as Error).message}. ` +
        `The container entrypoint should have written it from API_BASE_URL.`,
    );
  }

  if (!response.ok) {
    throw new ConfigError(
      `/config.json returned HTTP ${response.status}. The container entrypoint ` +
        `writes this file from API_BASE_URL at startup — check that the ` +
        `ui-customer service has API_BASE_URL set.`,
    );
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    throw new ConfigError("/config.json is not valid JSON.");
  }

  const apiBaseUrl = (parsed as Partial<RuntimeConfig>)?.apiBaseUrl;
  if (typeof apiBaseUrl !== "string" || apiBaseUrl.length === 0) {
    throw new ConfigError("/config.json is missing a non-empty 'apiBaseUrl' string.");
  }

  assertSafeApiBaseUrl(apiBaseUrl, window.location.origin);

  cached = { apiBaseUrl: apiBaseUrl.replace(/\/+$/, "") };
  return cached;
}

/**
 * The resolved config. Throws if called before {@link loadRuntimeConfig}
 * settles — that ordering is enforced in main.tsx, so a throw here is a bug,
 * not an operator error.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error("Runtime config accessed before loadRuntimeConfig() completed.");
  }
  return cached;
}

/** Test seam — seeds or clears the module-level cache between cases. */
export function __setRuntimeConfigForTests(config: RuntimeConfig | null): void {
  cached = config;
}
